import React, { useState, useEffect } from "react";
import { Modal, Button, message, Form } from "antd";
import {
  MailOutlined,
  KeyOutlined,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import FloatInput from "../util/FloatInput";
import "./ForgotPasswordModal.css";
import { httpClient } from "../util/Api";

const encryptPassword = (password) => {
  const key = "SSA-SOFT-2024";
  let encrypted = "";
  for (let i = 0; i < password.length; i++) {
    encrypted += String.fromCharCode(
      password.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }
  return btoa(encrypted);
};

export const ForgotPasswordModal = ({
  visible,
  onClose,
  setForgotPasswordVisible,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [submittedUsername, setSubmittedUsername] = useState("");

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setLoading(false);
    }
  }, [visible, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        userName: values.userName,
        email: values.email,
      };

      const response = await httpClient.post("Login/SendResetOtp", payload);

      let responseMsg = "";
      if (typeof response.data === "string") {
        responseMsg = response.data;
      } else if (response.data?.msg) {
        responseMsg = response.data.msg;
      } else if (response.data?.message) {
        responseMsg = response.data.message;
      } else {
        responseMsg = "OTP sent successfully!";
      }

      const isError =
        responseMsg.toLowerCase().includes("error") ||
        responseMsg.toLowerCase().includes("failed") ||
        responseMsg.toLowerCase().includes("invalid") ||
        responseMsg.toLowerCase().includes("not found");

      setLoading(false);

      if (isError) {
        message.error(responseMsg, 3);
      } else {
        message.success(responseMsg || "OTP sent to your email!", 3);
        setSubmittedUsername(values.userName);
        form.resetFields();
        onClose();
        setShowVerifyModal(true);
      }
    } catch (error) {
      setLoading(false);
      const errorMsg =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to send OTP. Please try again.";
      message.error(errorMsg, 3);
    }
  };

  const handleCancel = () => {
    setLoading(false);
    form.resetFields();
    onClose();
  };

  return (
    <>
      {visible === true ? (
        <Modal
          title={
            <div className="forgot-modal-header">
              <MailOutlined className="forgot-modal-icon" />
              <span>Forgot Password</span>
            </div>
          }
          open={visible}
          visible={visible}
          onCancel={handleCancel}
          closable={!loading}
          maskClosable={false}
          keyboard={!loading}
          footer={null}
          className="forgot-password-modal"
          centered
          destroyOnClose={true}
          width={480}
          afterClose={() => form.resetFields()}
        >
          <div className="forgot-modal-subtitle">
            Enter your username and email address to receive a password reset
            OTP.
          </div>

          <Form
            form={form}
            onFinish={onFinish}
            className="forgot-password-form"
          >
            <Form.Item
              name="userName"
              rules={[
                { required: true, message: "Please input your username" },
              ]}
            >
              <FloatInput
                label="Username"
                placeholder="Enter your username"
                name="userName"
                required
                maxLength={50}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please input your email" },
                {
                  type: "email",
                  message: "Please enter a valid email address",
                },
              ]}
            >
              <FloatInput
                label="Email Address"
                placeholder="Enter your email"
                name="email"
                required
                maxLength={100}
                disabled={loading}
              />
            </Form.Item>

            <div className="forgot-modal-actions">
              <Button
                type="default"
                onClick={handleCancel}
                size="large"
                className="forgot-cancel-btn"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className="forgot-submit-btn"
              >
                Send OTP
              </Button>
            </div>
          </Form>
        </Modal>
      ) : null}
      <VerifyOtpModal
        visible={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        username={submittedUsername}
      />
    </>
  );
};

const VerifyOtpModal = ({ visible, onClose, username }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setLoading(false);
      setNewPassword("");
      setConfirmPassword("");
      setPasswordsMatch(false);
    } else if (visible && username) {
      form.setFieldsValue({ userName: username });
    }
  }, [visible, username, form]);

  useEffect(() => {
    if (newPassword && confirmPassword) {
      setPasswordsMatch(
        newPassword === confirmPassword && newPassword.length >= 8,
      );
    } else {
      setPasswordsMatch(false);
    }
  }, [newPassword, confirmPassword]);

  const validatePassword = (_, value) => {
    if (!value) return Promise.reject(new Error("Please input new password"));

    if (value.length < 8)
      return Promise.reject(
        new Error("Password must be at least 8 characters"),
      );
    if (!/[A-Z]/.test(value))
      return Promise.reject(
        new Error("Password must contain at least 1 capital letter"),
      );
    if (!/[a-z]/.test(value))
      return Promise.reject(
        new Error("Password must contain at least 1 lowercase letter"),
      );
    if (!/[0-9]/.test(value))
      return Promise.reject(
        new Error("Password must contain at least 1 number"),
      );
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value))
      return Promise.reject(
        new Error("Password must contain at least 1 special character"),
      );

    return Promise.resolve();
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: "", color: "" };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength < 3) return { level: 1, text: "Weak", color: "#ff4d4f" };
    if (strength < 5) return { level: 2, text: "Medium", color: "#faad14" };
    return { level: 3, text: "Strong", color: "#52c41a" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        userName: values.userName,
        otp: values.otp,
        newPassword: encryptPassword(values.newPassword),
      };

      const response = await httpClient.post(
        "Login/VerifyOtpAndResetPassword",
        payload,
      );

      let responseMsg = "";
      if (typeof response.data === "string") {
        responseMsg = response.data;
      } else if (response.data?.msg) {
        responseMsg = response.data.msg;
      } else if (response.data?.message) {
        responseMsg = response.data.message;
      } else {
        responseMsg = "Password reset successfully!";
      }

      const isError =
        responseMsg.toLowerCase().includes("error") ||
        responseMsg.toLowerCase().includes("failed") ||
        responseMsg.toLowerCase().includes("invalid") ||
        responseMsg.toLowerCase().includes("incorrect") ||
        responseMsg.toLowerCase().includes("expired");

      setLoading(false);

      if (isError) {
        message.error(responseMsg, 3);
      } else {
        form.resetFields();
        onClose();
        message.success(responseMsg || "Password reset successfully!", 3);
        setTimeout(() => {
          message.info("Please login with your new password.", 2);
        }, 500);
      }
    } catch (error) {
      setLoading(false);
      const errorMsg =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to reset password. Please try again.";
      message.error(errorMsg, 3);
    }
  };

  const handleCancel = () => {
    setLoading(false);
    form.resetFields();
    onClose();
  };

  return (
    <>
      {visible === true ? (
        <Modal
          title={
            <div className="forgot-modal-header">
              <KeyOutlined className="forgot-modal-icon" />
              <span>Verify OTP & Reset Password</span>
            </div>
          }
          open={visible}
          visible={visible}
          onCancel={handleCancel}
          closable={!loading}
          maskClosable={false}
          keyboard={!loading}
          footer={null}
          className="forgot-password-modal"
          centered
          destroyOnClose={true}
          width={480}
          afterClose={() => form.resetFields()}
        >
          <div className="forgot-modal-subtitle">
            Enter the OTP sent to your email along with your new password.
          </div>

          <Form
            form={form}
            onFinish={onFinish}
            className="forgot-password-form"
          >
            <Form.Item
              name="userName"
              rules={[
                { required: true, message: "Please input your username" },
              ]}
            >
              <FloatInput
                label="Username"
                placeholder="Username"
                name="userName"
                required
                maxLength={50}
                disabled={true}
              />
            </Form.Item>

            <Form.Item
              name="otp"
              rules={[
                { required: true, message: "Please input the OTP" },
                { len: 6, message: "OTP must be 6 digits" },
                {
                  pattern: /^[0-9]+$/,
                  message: "OTP must contain only numbers",
                },
              ]}
            >
              <FloatInput
                label="OTP Code"
                placeholder="Enter 6-digit OTP"
                name="otp"
                required
                maxLength={6}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              rules={[{ validator: validatePassword }]}
            >
              <div className="forgot-field-wrapper">
                <FloatInput
                  label="New Password"
                  placeholder="Enter new password"
                  name="newPassword"
                  type="password"
                  required
                  maxLength={30}
                  disabled={loading}
                  onChange={(value) => {
                    setNewPassword(value);
                    form.setFieldsValue({ newPassword: value });
                  }}
                />
                {newPassword && (
                  <div className="forgot-strength-indicator">
                    <div className="forgot-strength-bar-container">
                      <div
                        className={`forgot-strength-bar forgot-strength-${passwordStrength.level}`}
                        style={{ backgroundColor: passwordStrength.color }}
                      />
                    </div>
                    <span
                      className="forgot-strength-text"
                      style={{ color: passwordStrength.color }}
                    >
                      {passwordStrength.text}
                    </span>
                  </div>
                )}
              </div>
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              validateStatus=""
              help=""
              rules={[
                { required: true, message: "" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(""));
                  },
                }),
              ]}
            >
              <div className="forgot-field-wrapper">
                <FloatInput
                  label="Confirm Password"
                  placeholder="Confirm new password"
                  name="confirmPassword"
                  type="password"
                  required
                  maxLength={30}
                  disabled={loading}
                  onChange={(value) => {
                    setConfirmPassword(value);
                    form.setFieldsValue({ confirmPassword: value });
                  }}
                />
                {confirmPassword && (
                  <div className="forgot-match-indicator">
                    {passwordsMatch ? (
                      <span className="forgot-match-icon success">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <circle cx="10" cy="10" r="10" fill="#52c41a" />
                          <path
                            d="M6 10L9 13L14 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Passwords match</span>
                      </span>
                    ) : (
                      confirmPassword.length > 0 && (
                        <span className="forgot-match-icon error">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <circle cx="10" cy="10" r="10" fill="#ff4d4f" />
                            <path
                              d="M7 7L13 13M13 7L7 13"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span>Passwords do not match</span>
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </Form.Item>

            {/* <div className="forgot-password-hint">
              Must contain: 8+ characters, uppercase, lowercase, digit, special
              character
            </div> */}

            <div className="forgot-modal-actions">
              <Button
                type="default"
                onClick={handleCancel}
                size="large"
                className="forgot-cancel-btn"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className="forgot-submit-btn"
              >
                Reset Password
              </Button>
            </div>
          </Form>
        </Modal>
      ) : null}
    </>
  );
};

export default ForgotPasswordModal;
